/**
 * Firebase Client - Cloud sync and real-time updates
 */

import { initializeApp } from 'firebase/app'
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore'
import { getSyncQueue, removeFromSyncQueue } from './storage.js'
import { generateUUID } from './uuid.js'

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyA2oYJ5ecVjgCdBntKb9kcO_1zmqt1NpCc",
  authDomain: "manatiming.firebaseapp.com",
  projectId: "manatiming",
  storageBucket: "manatiming.firebasestorage.app",
  messagingSenderId: "356275735229",
  appId: "1:356275735229:web:dd58d3ad1aaf81a96885d4"
}

let app = null
let db = null
let syncInterval = null
let connectionStatus = 'offline'
const SYNC_INTERVAL_MS = 5000 // 5 seconds

/**
 * Initialize Firebase client
 */
export function initFirebase() {
  try {
    app = initializeApp(firebaseConfig)
    db = getFirestore(app)
    connectionStatus = 'connected'

    // Start the background sync worker
    startSyncWorker()

    console.log('Firebase initialized successfully')
    return db
  } catch (err) {
    console.error('Firebase initialization failed:', err)
    connectionStatus = 'error'
    return null
  }
}

/**
 * Get current connection status
 */
export function getConnectionStatus() {
  return connectionStatus
}

/**
 * Start the background sync worker
 */
function startSyncWorker() {
  if (syncInterval) {
    clearInterval(syncInterval)
  }

  // Run immediately on start
  processSyncQueue()

  // Then run every 5 seconds
  syncInterval = setInterval(processSyncQueue, SYNC_INTERVAL_MS)

  console.log('Sync worker started (interval: 5s)')
}



/**
 * CLOUD-FIRST: Reset race results directly from Firebase
 * Deletes timestamps and bibEntries, but keeps roster and race
 */
export async function resetRaceResultsFromCloud(raceId) {
  if (!db) return false

  try {
    const batch = writeBatch(db)

    // Delete all timestamps for this race
    const timestampsQuery = query(collection(db, 'timestamps'), where('raceId', '==', raceId))
    const timestampsSnapshot = await getDocs(timestampsQuery)
    timestampsSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'timestamps', docSnap.id))
    })
    console.log(`Queued ${timestampsSnapshot.size} timestamps for deletion`)

    // Delete all bibEntries for this race
    const bibEntriesQuery = query(collection(db, 'bibEntries'), where('raceId', '==', raceId))
    const bibEntriesSnapshot = await getDocs(bibEntriesQuery)
    bibEntriesSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'bibEntries', docSnap.id))
    })
    console.log(`Queued ${bibEntriesSnapshot.size} bibEntries for deletion`)

    // Reset race status to 'ready' and clear execution times
    batch.update(doc(db, 'races', raceId), {
      status: 'ready',
      startTime: null,
      gunTime: null,
      updatedAt: serverTimestamp()
    })

    await batch.commit()
    console.log(`Successfully reset results for race ${raceId}`)
    return true
  } catch (err) {
    console.error('Error resetting race results:', err)
    return false
  }
}

/**
 * Stop the sync worker
 */
export function stopSyncWorker() {
  if (syncInterval) {
    clearInterval(syncInterval)
    syncInterval = null
    console.log('Sync worker stopped')
  }
}

/**
 * Process the sync queue - push pending items to Firebase
 */
async function processSyncQueue() {
  if (!db) {
    connectionStatus = 'offline'
    return
  }

  const queue = getSyncQueue()
  if (queue.length === 0) return

  console.log(`Sync worker: processing ${queue.length} items`)

  for (const item of queue) {
    try {
      let success = false

      switch (item.type) {
        case 'race_start':
          success = await syncRaceStart(item.data)
          break

        case 'race_save':
          success = await syncRace(item.data)
          break

        case 'race_delete':
          success = await deleteRace(item.data.id)
          break

        case 'race_status':
          success = await updateRaceStatus(item.data.id, item.data.status)
          break

        case 'timestamp':
        case 'timestamp_insert':
        case 'timestamp_update':
          success = await syncTimestamp(item.data)
          break

        case 'timestamp_delete':
          success = await deleteTimestamp(item.data.id)
          break

        case 'timestamps_resequence':
          success = await resequenceTimestamps(item.data.timestamps)
          break

        case 'bib_entry':
          success = await syncBibEntry(item.data)
          break

        case 'roster_upload':
          success = await syncRosterUpload(item.data)
          break

        case 'race_reset_results':
          success = await resetRaceResultsFromCloud(item.data.id)
          break

        default:
          console.warn(`Unknown sync type: ${item.type}`)
          success = true // Remove unknown items
      }

      if (success) {
        removeFromSyncQueue(item.id)
        connectionStatus = 'connected'
      } else {
        connectionStatus = 'error'
      }
    } catch (err) {
      console.error(`Sync exception for ${item.type}:`, err)
      connectionStatus = 'error'
    }
  }
}

/**
 * Sync a race to Firebase
 */
async function syncRace(race) {
  if (!db) return false

  try {
    const raceData = {
      id: race.id,
      name: race.name,
      date: race.date,
      distanceValue: race.distanceValue || null,
      distanceUnit: race.distanceUnit || null,
      ageGroupPreset: race.ageGroupPreset || 'standard',
      status: race.status || 'draft',
      startTime: race.startTime || null,
      gunTime: race.gunTime || null,
      updatedAt: serverTimestamp()
    }

    await setDoc(doc(db, 'races', race.id), raceData, { merge: true })
    return true
  } catch (err) {
    console.error('Error syncing race:', err)
    return false
  }
}

/**
 * Sync race start data to Firebase (minimal fields only)
 * Called when race is started - only updates start-related fields
 * without overwriting existing race name/date
 */
async function syncRaceStart(data) {
  if (!db) return false

  try {
    const startData = {
      status: data.status || 'live',
      startTime: data.startTime,
      updatedAt: serverTimestamp()
    }

    // Only include optional fields if they exist
    if (data.startedAt) {
      startData.startedAt = data.startedAt
    }
    if (data.deviceId) {
      startData.deviceId = data.deviceId
    }

    await setDoc(doc(db, 'races', data.id), startData, { merge: true })
    console.log('Race start synced to Firebase:', data.id, startData.status)
    return true
  } catch (err) {
    console.error('Error syncing race start:', err)
    return false
  }
}

/**
 * Delete a race and ALL its related data from Firebase
 * This includes: timestamps, bibEntries, athletes, and the race itself
 */
async function deleteRace(raceId) {
  if (!db) return false

  try {
    const batch = writeBatch(db)

    // Delete all timestamps for this race
    const timestampsQuery = query(collection(db, 'timestamps'), where('raceId', '==', raceId))
    const timestampsSnapshot = await getDocs(timestampsQuery)
    timestampsSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'timestamps', docSnap.id))
    })
    console.log(`Queued ${timestampsSnapshot.size} timestamps for deletion`)

    // Delete all bibEntries for this race
    const bibEntriesQuery = query(collection(db, 'bibEntries'), where('raceId', '==', raceId))
    const bibEntriesSnapshot = await getDocs(bibEntriesQuery)
    bibEntriesSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'bibEntries', docSnap.id))
    })
    console.log(`Queued ${bibEntriesSnapshot.size} bibEntries for deletion`)

    // Delete all athletes for this race
    const athletesQuery = query(collection(db, 'athletes'), where('raceId', '==', raceId))
    const athletesSnapshot = await getDocs(athletesQuery)
    athletesSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'athletes', docSnap.id))
    })
    console.log(`Queued ${athletesSnapshot.size} athletes for deletion`)

    // Delete any locks for this race
    const locksQuery = query(collection(db, 'locks'), where('raceId', '==', raceId))
    const locksSnapshot = await getDocs(locksQuery)
    locksSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'locks', docSnap.id))
    })

    // Delete the race document itself
    batch.delete(doc(db, 'races', raceId))

    // Commit all deletions in one batch
    await batch.commit()
    console.log(`Successfully deleted race ${raceId} and all related data`)

    return true
  } catch (err) {
    console.error('Error deleting race and related data:', err)
    return false
  }
}

/**
 * Update race status
 */
async function updateRaceStatus(raceId, status) {
  if (!db) return false

  try {
    await updateDoc(doc(db, 'races', raceId), {
      status,
      updatedAt: serverTimestamp()
    })
    return true
  } catch (err) {
    console.error('Error updating race status:', err)
    return false
  }
}

/**
 * Sync a timestamp to Firebase
 */
async function syncTimestamp(timestamp) {
  if (!db) return false

  try {
    const timestampData = {
      id: timestamp.id,
      raceId: timestamp.raceId,
      rawTime: timestamp.rawTime,
      netTime: timestamp.netTime,
      sequence: timestamp.sequence,
      deviceId: timestamp.deviceId,
      recordedAt: timestamp.recordedAt,
      inserted: timestamp.inserted || false,
      manual: timestamp.manual || false,
      corrected: timestamp.corrected || false,
      deleted: timestamp.deleted || false,
      updatedAt: serverTimestamp()
    }

    await setDoc(doc(db, 'timestamps', timestamp.id), timestampData, { merge: true })
    return true
  } catch (err) {
    console.error('Error syncing timestamp:', err)
    return false
  }
}

/**
 * Delete a timestamp (soft delete)
 */
async function deleteTimestamp(id) {
  if (!db) return false

  try {
    await updateDoc(doc(db, 'timestamps', id), {
      deleted: true,
      updatedAt: serverTimestamp()
    })
    return true
  } catch (err) {
    console.error('Error deleting timestamp:', err)
    return false
  }
}

/**
 * Resequence timestamps
 */
async function resequenceTimestamps(timestamps) {
  if (!db) return false

  try {
    const batch = writeBatch(db)

    timestamps.forEach(t => {
      const ref = doc(db, 'timestamps', t.id)
      batch.update(ref, { sequence: t.sequence })
    })

    await batch.commit()
    return true
  } catch (err) {
    console.error('Error resequencing timestamps:', err)
    return false
  }
}

/**
 * Sync a bib entry to Firebase
 */
async function syncBibEntry(entry) {
  if (!db) return false

  try {
    const entryData = {
      id: entry.id,
      raceId: entry.raceId,
      bib: entry.bib,
      sequence: entry.sequence,
      enteredAt: entry.enteredAt,
      updatedAt: serverTimestamp()
    }

    await setDoc(doc(db, 'bibEntries', entry.id), entryData, { merge: true })
    return true
  } catch (err) {
    console.error('Error syncing bib entry:', err)
    return false
  }
}

/**
 * Sync roster upload from queue
 */
async function syncRosterUpload(data) {
  if (!db || !data.raceId || !data.athletes) return false

  try {
    const result = await uploadRoster(data.raceId, data.athletes)
    if (result.error) {
      console.error('Roster upload failed:', result.error)
      return false
    }
    console.log(`Roster synced: ${data.athletes.length} athletes for race ${data.raceId}`)
    return true
  } catch (err) {
    console.error('Roster sync error:', err)
    return false
  }
}

/**
 * Upload roster to Firebase
 */
export async function uploadRoster(raceId, athletes) {
  if (!db) return { error: 'offline' }

  try {
    const batch = writeBatch(db)

    athletes.forEach(athlete => {
      const athleteId = athlete.id || generateUUID()
      const ref = doc(db, 'athletes', athleteId)
      batch.set(ref, {
        ...athlete,
        id: athleteId,
        raceId,
        updatedAt: serverTimestamp()
      }, { merge: true })
    })

    await batch.commit()
    return { error: null }
  } catch (err) {
    console.error('Error uploading roster:', err)
    return { error: err.message }
  }
}

/**
 * CLOUD-FIRST: Create or update a race directly in Firebase
 * Returns the saved race immediately after Firebase write completes
 */
export async function saveRaceToCloud(race) {
  if (!db) {
    console.error('Firebase not initialized')
    return null
  }

  try {
    const raceId = race.id || generateUUID()
    const raceData = {
      id: raceId,
      name: race.name,
      date: race.date,
      distanceValue: race.distanceValue || null,
      distanceUnit: race.distanceUnit || null,
      ageGroupPreset: race.ageGroupPreset || 'standard',
      raceType: race.raceType || 'road',
      status: race.status || 'draft',
      startTime: race.startTime || null,
      gunTime: race.gunTime || null,
      createdAt: race.createdAt || Date.now(),
      updatedAt: Date.now()
    }

    await setDoc(doc(db, 'races', raceId), raceData, { merge: true })
    console.log('Race saved to Firebase:', raceId)
    return { ...raceData, id: raceId }
  } catch (err) {
    console.error('Error saving race to Firebase:', err)
    return null
  }
}

/**
 * CLOUD-FIRST: Delete a race directly from Firebase
 */
export async function deleteRaceFromCloud(raceId) {
  if (!db) return false

  try {
    // Use the existing deleteRace function which handles cascading deletes
    const batch = writeBatch(db)

    // Delete all timestamps for this race
    const timestampsQuery = query(collection(db, 'timestamps'), where('raceId', '==', raceId))
    const timestampsSnapshot = await getDocs(timestampsQuery)
    timestampsSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'timestamps', docSnap.id))
    })

    // Delete all bibEntries for this race
    const bibEntriesQuery = query(collection(db, 'bibEntries'), where('raceId', '==', raceId))
    const bibEntriesSnapshot = await getDocs(bibEntriesQuery)
    bibEntriesSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'bibEntries', docSnap.id))
    })

    // Delete all athletes for this race
    const athletesQuery = query(collection(db, 'athletes'), where('raceId', '==', raceId))
    const athletesSnapshot = await getDocs(athletesQuery)
    athletesSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'athletes', docSnap.id))
    })

    // Delete any locks for this race
    const locksQuery = query(collection(db, 'locks'), where('raceId', '==', raceId))
    const locksSnapshot = await getDocs(locksQuery)
    locksSnapshot.forEach(docSnap => {
      batch.delete(doc(db, 'locks', docSnap.id))
    })

    // Delete the race document itself
    batch.delete(doc(db, 'races', raceId))

    await batch.commit()
    console.log('Race deleted from Firebase:', raceId)
    return true
  } catch (err) {
    console.error('Error deleting race from Firebase:', err)
    return false
  }
}

/**
 * CLOUD-FIRST: Upload roster directly to Firebase
 */
export async function uploadRosterToCloud(raceId, athletes) {
  if (!db || !raceId) return { error: 'No database or race ID' }

  try {
    const batch = writeBatch(db)

    athletes.forEach(athlete => {
      const athleteId = athlete.id || generateUUID()
      const ref = doc(db, 'athletes', athleteId)
      batch.set(ref, {
        ...athlete,
        id: athleteId,
        raceId,
        updatedAt: serverTimestamp()
      }, { merge: true })
    })

    await batch.commit()
    console.log(`Roster uploaded to Firebase: ${athletes.length} athletes for race ${raceId}`)
    return { error: null, count: athletes.length }
  } catch (err) {
    console.error('Error uploading roster to Firebase:', err)
    return { error: err.message }
  }
}

/**
 * Subscribe to real-time timestamp updates for a race
 */
export function subscribeToTimestamps(raceId, callback) {
  if (!db || !raceId) return null

  // Simple query without orderBy to avoid composite index requirement
  const q = query(
    collection(db, 'timestamps'),
    where('raceId', '==', raceId)
  )

  return onSnapshot(q, (snapshot) => {
    const timestamps = []
    snapshot.forEach(docSnap => {
      const data = docSnap.data()
      // Filter out deleted timestamps client-side
      if (!data.deleted) {
        timestamps.push(data)
      }
    })
    // Sort client-side by sequence
    timestamps.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    callback(timestamps)
  }, (err) => {
    console.error('Timestamp subscription error:', err)
    // Return empty array on error so UI still works
    callback([])
  })
}

/**
 * Subscribe to real-time bib entry updates for a race
 */
export function subscribeToBibEntries(raceId, callback) {
  if (!db || !raceId) return null

  // Simple query without orderBy to avoid composite index requirement
  const q = query(
    collection(db, 'bibEntries'),
    where('raceId', '==', raceId)
  )

  return onSnapshot(q, (snapshot) => {
    const entries = []
    snapshot.forEach(docSnap => {
      entries.push(docSnap.data())
    })
    // Sort client-side by sequence
    entries.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    callback(entries)
  }, (err) => {
    console.error('Bib entries subscription error:', err)
    // Return empty array on error so UI still works
    callback([])
  })
}

/**
 * Subscribe to race updates
 */
export function subscribeToRace(raceId, callback) {
  if (!db) return null

  return onSnapshot(doc(db, 'races', raceId), (doc) => {
    if (doc.exists()) {
      callback(doc.data())
    }
  }, (err) => {
    console.error('Race subscription error:', err)
  })
}

/**
 * Subscribe to ALL races (real-time list)
 * This is the cloud-first approach - races come from Firebase, not localStorage
 */
export function subscribeToAllRaces(callback) {
  if (!db) return null

  return onSnapshot(collection(db, 'races'), (snapshot) => {
    const races = []
    snapshot.forEach(docSnap => {
      races.push(docSnap.data())
    })
    // Sort by date descending (most recent first)
    races.sort((a, b) => {
      const dateA = a.date || a.createdAt || 0
      const dateB = b.date || b.createdAt || 0
      return dateB - dateA
    })
    console.log('Races subscription update:', races.length, 'races')
    callback(races)
  }, (err) => {
    console.error('Races subscription error:', err)
    callback([])
  })
}

/**
 * Fetch all races from Firebase and save to local storage
 * Call this on app startup to sync races across devices
 */
export async function fetchRacesFromCloud() {
  if (!db) return []

  try {
    const snapshot = await getDocs(collection(db, 'races'))
    const races = []
    snapshot.forEach(docSnap => {
      races.push(docSnap.data())
    })
    console.log('Fetched races from cloud:', races.length)
    return races
  } catch (err) {
    console.error('Error fetching races:', err)
    return []
  }
}

/**
 * Fetch all athletes for a race from Firebase
 */
export async function fetchAthletesFromCloud(raceId) {
  if (!db || !raceId) return []

  try {
    const q = query(
      collection(db, 'athletes'),
      where('raceId', '==', raceId)
    )
    const snapshot = await getDocs(q)
    const athletes = []
    snapshot.forEach(docSnap => {
      athletes.push(docSnap.data())
    })
    console.log('Fetched athletes from cloud:', athletes.length)
    return athletes
  } catch (err) {
    console.error('Error fetching athletes:', err)
    return []
  }
}

/**
 * Fetch ALL athletes from ALL races
 * Used to enable cross-race bib lookup (for runners in multiple events)
 */
export async function fetchAllAthletesFromCloud() {
  if (!db) return {}

  try {
    const snapshot = await getDocs(collection(db, 'athletes'))
    const athletesByRace = {}
    snapshot.forEach(docSnap => {
      const athlete = docSnap.data()
      const raceId = athlete.raceId
      if (raceId) {
        if (!athletesByRace[raceId]) {
          athletesByRace[raceId] = []
        }
        athletesByRace[raceId].push(athlete)
      }
    })
    console.log('Fetched all athletes from cloud:', snapshot.size, 'across', Object.keys(athletesByRace).length, 'races')
    return athletesByRace
  } catch (err) {
    console.error('Error fetching all athletes:', err)
    return {}
  }
}

/**
 * Subscribe to athletes for a race (real-time)
 */
export function subscribeToAthletes(raceId, callback) {
  if (!db || !raceId) return null

  const q = query(
    collection(db, 'athletes'),
    where('raceId', '==', raceId)
  )

  return onSnapshot(q, (snapshot) => {
    const athletes = []
    snapshot.forEach(docSnap => {
      athletes.push(docSnap.data())
    })
    console.log('Athletes subscription update:', athletes.length, 'athletes for race', raceId)
    callback(athletes)
  }, (err) => {
    console.error('Athletes subscription error:', err)
    callback([])
  })
}

/**
 * Fetch all timestamps for a race
 */
export async function fetchTimestamps(raceId) {
  if (!db) return []

  try {
    const q = query(
      collection(db, 'timestamps'),
      where('raceId', '==', raceId)
    )

    const snapshot = await getDocs(q)
    const timestamps = []
    snapshot.forEach(docSnap => {
      const data = docSnap.data()
      if (!data.deleted) {
        timestamps.push(data)
      }
    })
    // Sort client-side
    timestamps.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    return timestamps
  } catch (err) {
    console.error('Error fetching timestamps:', err)
    return []
  }
}

/**
 * Fetch all bib entries for a race
 */
export async function fetchBibEntries(raceId) {
  if (!db) return []

  try {
    const q = query(
      collection(db, 'bibEntries'),
      where('raceId', '==', raceId)
    )

    const snapshot = await getDocs(q)
    const entries = []
    snapshot.forEach(docSnap => {
      entries.push(docSnap.data())
    })
    // Sort client-side
    entries.sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
    return entries
  } catch (err) {
    console.error('Error fetching bib entries:', err)
    return []
  }
}

/**
 * Claim the Master Timer Lock for a race
 */
export async function claimTimerLock(raceId, deviceId) {
  if (!db || !raceId) return false
  try {
    const lockRef = doc(db, 'locks', `timer_${raceId}`)
    await setDoc(lockRef, {
      raceId,
      masterDeviceId: deviceId,
      claimedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    })
    return true
  } catch (err) {
    console.error('Error claiming timer lock:', err)
    return false
  }
}

/**
 * Release the Master Timer Lock
 */
export async function releaseTimerLock(raceId) {
  if (!db || !raceId) return false
  try {
    await deleteDoc(doc(db, 'locks', `timer_${raceId}`))
    return true
  } catch (err) {
    console.error('Error releasing timer lock:', err)
    return false
  }
}

/**
 * Subscribe to the Master Timer Lock updates
 */
export function subscribeToTimerLock(raceId, callback) {
  if (!db || !raceId) return null
  const lockRef = doc(db, 'locks', `timer_${raceId}`)
  return onSnapshot(lockRef, (docSnap) => {
    callback(docSnap.exists() ? docSnap.data() : null)
  }, (err) => {
    console.error('Timer lock subscription error:', err)
  })
}

/**
 * ADMIN: Clear ALL data from Firebase (immediate, not queued)
 * Use this for pre-race cleanup when queue-based deletion isn't working
 */
export async function clearAllFirebaseData() {
  if (!db) {
    console.error('Firebase not initialized')
    return { success: false, error: 'Firebase not initialized' }
  }

  console.log('Starting Firebase data cleanup...')
  const results = { races: 0, timestamps: 0, bibEntries: 0, athletes: 0, locks: 0 }

  try {
    // Delete all collections in batches (Firestore limit is 500 per batch)
    const collections = ['races', 'timestamps', 'bibEntries', 'athletes', 'locks']

    for (const collectionName of collections) {
      console.log(`Deleting ${collectionName}...`)
      const snapshot = await getDocs(collection(db, collectionName))

      // Process in batches of 400 to stay under limit
      const docs = snapshot.docs
      for (let i = 0; i < docs.length; i += 400) {
        const batch = writeBatch(db)
        const chunk = docs.slice(i, i + 400)
        chunk.forEach(docSnap => {
          batch.delete(doc(db, collectionName, docSnap.id))
        })
        await batch.commit()
        results[collectionName] = (results[collectionName] || 0) + chunk.length
      }
      console.log(`Deleted ${results[collectionName] || 0} documents from ${collectionName}`)
    }

    console.log('Firebase cleanup complete:', results)
    return { success: true, results }
  } catch (err) {
    console.error('Firebase cleanup error:', err)
    return { success: false, error: err.message }
  }
}

export { db }
